"use client";

import { useState } from "react";
import {
  ErrorNotice,
  Notice,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  RetractionResults,
  type RetractionRow,
  retractionCsv,
} from "@/components/tools/identifier-tools/retraction-results";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadText } from "@/lib/tool-kit/download";
import { planBatch } from "@/lib/tool-kit/identifiers/batch";
import {
  type FoundDoi,
  type MalformedDoi,
  scanDois,
} from "@/lib/tool-kit/identifiers/doi-text";
import {
  CROSSREF_BATCH_SIZE,
  createBatchedLookup,
  crossrefWorksByDoi,
} from "@/lib/tool-kit/identifiers/lookup";
import { assessWork } from "@/lib/tool-kit/identifiers/retraction";
import { useLookupRun } from "@/lib/tool-kit/identifiers/use-lookup-run";

/** English strings. A translated content file overrides these through `ui`. */
export const RETRACTION_CHECKER_STRINGS = {
  inputLabel: "DOIs or a reference list",
  inputPlaceholder:
    "Paste DOIs, one per line, or a whole reference list in any style. Every DOI in it is checked.",
  inputHint:
    "doi:10.…, DOI: 10.…, https://doi.org/10.… and bare 10.… are all found. Up to {max} DOIs per check.",
  trySample: "Try a sample",
  sample:
    "Joly, J. F., Stapel, D. A., & Lindenberg, S. M. (2008). Silence and table manners: When environments activate norms. Personality and Social Psychology Bulletin, 34(8), 1047–1056. https://doi.org/10.1177/0146167208318401\nWakefield AJ, Murch SH, Anthony A, et al. Ileal-lymphoid-nodular hyperplasia, non-specific colitis, and pervasive developmental disorder in children. Lancet. 1998;351(9103):637-641. doi:10.1016/S0140-6736(97)11096-0\nM. Aspelmeyer, “Measured measurement,” Nature Phys., vol. 5, no. 1, pp. 11–12, Jan. 2009, doi: 10.1038/nphys1170.\nBazhin AV, Savchenko MS, Shifrina ON, et al. Recoverin as a paraneoplastic antigen in lung cancer: the occurrence of anti-recoverin autoantibodies in sera and recoverin in tumors. Lung Cancer. 2004;44(2):193-198. doi:10.1016/j.lungcan.2003.10.006",
  check: "Check for retractions",
  checking: "Checking {done} of {total}…",
  privacy:
    "Only the DOIs leave this page: your browser sends them to Crossref's public API. The rest of your list is not sent anywhere.",
  emptyPrompt: "Paste a reference list or some DOIs, then check them.",
  noDois:
    "No DOIs found. The checker works from DOIs, so a reference without one cannot be checked here — search the Retraction Watch Database for its title instead.",
  results: "Results",
  checkedCount:
    "one:{count} DOI looked up in Crossref|other:{count} DOIs looked up in Crossref",
  statSerious: "Retracted or withdrawn",
  statConcern: "Expression of concern",
  statCorrected: "Corrected or updated",
  statNone: "No notice found",
  uncheckedCount:
    "one:{count} DOI could not be checked — the table says why.|other:{count} DOIs could not be checked — the table says why.",
  onlyNotices: "Show only DOIs with a notice",
  noneToShow: "No DOI in this list has a notice recorded in Crossref.",
  colDoi: "DOI",
  colTitle: "Title",
  colStatus: "Status",
  colNotices: "Notices",
  lineRef: "Line {line}",
  untitled: "(no title in Crossref)",
  statusRetracted: "Retracted",
  statusWithdrawn: "Withdrawn",
  statusPartial: "Partly retracted",
  statusConcern: "Expression of concern",
  statusCorrected: "Corrected",
  statusUpdated: "Other update",
  statusNone: "No notice found",
  statusNotice: "Is itself a notice",
  statusNotInCrossref: "Not in Crossref",
  statusUnchecked: "Not checked",
  typeRetraction: "Retraction",
  typePartialRetraction: "Partial retraction",
  typeWithdrawal: "Withdrawal",
  typeRemoval: "Removal",
  typeConcern: "Expression of concern",
  typeCorrection: "Correction",
  typeErratum: "Erratum",
  typeCorrigendum: "Corrigendum",
  sourcePublisher: "Publisher",
  sourceRetractionWatch: "Retraction Watch",
  sourceRecord: "Retraction Watch, record {id}",
  undated: "undated",
  sameDoi: "notice at this DOI",
  noticeFor: "{type} notice for",
  failNotFound:
    "Crossref has no record of this DOI. It may be registered with another agency, such as DataCite, or mistyped — this check cannot cover it.",
  failRateLimited:
    "Crossref is limiting requests. Wait a minute and check again.",
  failUnavailable: "Crossref did not answer properly. Try again shortly.",
  failNetwork: "Crossref could not be reached — check your connection.",
  overLimit: "Over the {max}-DOI limit — check it in a second batch.",
  problemCharacter:
    "Contains an unusual character — compare it with the publisher's page.",
  problemSplit:
    "Ends in a hyphen at the end of a line, so it may continue on the next line.",
  malformedHeading: "Not checked — incomplete DOIs",
  malformedItem: "Line {line}: {written}",
  caveat:
    "“No notice found” means Crossref records no notice, not that the work is sound. Before you rely on a source, check the publisher's page and the Retraction Watch Database.",
  downloadCsv: "Download CSV",
  csvDoi: "DOI",
  csvTitle: "Title",
  csvStatus: "Status",
  csvNotice: "Notice",
  csvDate: "Notice date",
  csvSource: "Source",
  csvNoticeDoi: "Notice DOI",
  unexpected: "Something went wrong during the check. Please try again.",
} as const;

const MAX_DOIS = 100;
/** Crossref allows three list queries a second in its polite pool; this stays well under. */
const SPACING_MS = 400;
const CSV_MIME = "text/csv;charset=utf-8";

type Row = RetractionRow;

function planRows(text: string): { rows: Row[]; malformed: MalformedDoi[] } {
  const scan = scanDois(text);
  const byDoi = new Map(scan.dois.map((found) => [found.doi, found]));
  const rows = planBatch(
    scan.dois.map((found) => found.doi),
    (doi) => byDoi.get(doi) as FoundDoi,
    (found) => found.doi.toLowerCase(),
    MAX_DOIS
  );
  return { rows, malformed: scan.malformed };
}

export default function RetractionCheckerWidget() {
  const strings = useToolUi(RETRACTION_CHECKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [malformed, setMalformed] = useState<MalformedDoi[]>([]);
  const [checked, setChecked] = useState(false);
  const { rows, progress, failed, run } = useLookupRun<Row>();

  function handleCheck() {
    const plan = planRows(text);
    setMalformed(plan.malformed);
    setChecked(true);
    const pending = plan.rows
      .filter((row) => row.state === "pending")
      .map((row) => row.parsed.doi);
    const lookup = createBatchedLookup(pending, crossrefWorksByDoi, {
      size: CROSSREF_BATCH_SIZE,
      spacingMs: SPACING_MS,
    });
    run(
      plan.rows,
      (row, signal) => lookup(row.parsed.doi, signal),
      (row, result): Row =>
        result.ok
          ? { ...row, state: "found", result: assessWork(result.value) }
          : { ...row, state: "failed", reason: result.reason }
    );
  }

  const running = progress !== null;
  const finished = checked && !running;
  const unchecked = rows.filter((row) => !row.result).length;
  const lookedUp = rows.filter((row) => row.state !== "over").length;

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={t(strings.inputHint, { max: MAX_DOIS })}
        id="rtc-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        prose
        rows={8}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-3">
        <button
          className={BUTTON_PRIMARY}
          disabled={running || text.trim() === ""}
          onClick={handleCheck}
          type="button"
        >
          {progress
            ? t(strings.checking, {
                done: fmt(progress.done),
                total: fmt(progress.total),
              })
            : strings.check}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={running}
          onClick={() => setText(strings.sample)}
          type="button"
        >
          {strings.trySample}
        </button>
      </div>
      <p className={HINT}>{strings.privacy}</p>

      <div aria-live="polite" className="space-y-6">
        {failed ? <ErrorNotice>{strings.unexpected}</ErrorNotice> : null}
        {checked ? null : <Notice>{strings.emptyPrompt}</Notice>}
        {finished && rows.length === 0 && malformed.length === 0 ? (
          <Notice>{strings.noDois}</Notice>
        ) : null}
        {finished && rows.length > 0 ? (
          <div className={`${RESULT_CARD} space-y-4`}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className={WIDGET_H2}>{strings.results}</h2>
                <p className="text-muted-foreground text-sm">
                  {tPlural(strings.checkedCount, locale, lookedUp, {
                    count: fmt(lookedUp),
                  })}
                </p>
              </div>
              <button
                className={BUTTON_SECONDARY}
                onClick={() =>
                  downloadText(
                    "retraction-check.csv",
                    retractionCsv(rows, strings),
                    CSV_MIME
                  )
                }
                type="button"
              >
                {strings.downloadCsv}
              </button>
            </div>
            {unchecked > 0 ? (
              <p className="text-muted-foreground text-sm">
                {tPlural(strings.uncheckedCount, locale, unchecked, {
                  count: fmt(unchecked),
                })}
              </p>
            ) : null}
            <RetractionResults max={MAX_DOIS} rows={rows} strings={strings} />
          </div>
        ) : null}
        {finished && malformed.length > 0 ? (
          <div className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.malformedHeading}</h3>
            <ul className="space-y-1 text-muted-foreground text-sm">
              {malformed.map((item) => (
                <li key={`${item.line}|${item.written}`}>
                  {t(strings.malformedItem, {
                    line: fmt(item.line),
                    written: item.written,
                  })}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </section>
  );
}
